import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Customers use the sky pill the engagement feed uses for companies; contacts a neutral one,
 * so the two read differently at a glance. Open tasks are amber, overdue red and completed green,
 * matching the task status badge.
 */
const TONES = {
  customer: { base: "bg-sky-50 text-sky-700 border-sky-200", hover: "hover:bg-sky-100" },
  contact: { base: "bg-gray-50 text-gray-700 border-gray-200", hover: "hover:bg-gray-100" },
  task: { base: "bg-amber-50 text-amber-800 border-amber-200", hover: "hover:bg-amber-100" },
  overdueTask: { base: "bg-red-50 text-red-700 border-red-200", hover: "hover:bg-red-100" },
  completedTask: { base: "bg-emerald-50 text-emerald-700 border-emerald-200", hover: "hover:bg-emerald-100" },
} as const;

const BASE =
  "inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-4 no-underline transition-colors";

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type MailPillProps = {
  tone: keyof typeof TONES;
  children: ReactNode;
  /** Shown on hover, e.g. the email address. */
  title?: string;
  href?: string;
  onClick?: () => void;
};

/**
 * A customer, contact or task on an email. A link or a button when it can be opened, plain text
 * otherwise, such as inside a list row where clicking should select the email instead.
 */
export const MailPill = ({ tone, children, title, href, onClick }: MailPillProps) => {
  const { base, hover } = TONES[tone];
  const content = <span className="truncate">{children}</span>;

  if (href) {
    return (
      <a href={href} title={title} className={cn(BASE, base, hover, FOCUS)}>
        {content}
      </a>
    );
  }

  if (onClick) {
    return (
      <button type="button" title={title} onClick={onClick} className={cn(BASE, base, hover, FOCUS)}>
        {content}
      </button>
    );
  }

  return (
    <span title={title} className={cn(BASE, base)}>
      {content}
    </span>
  );
};
